import { useState, useEffect, useCallback } from 'react';
import useMenu from 'data/hooks/useMenu';
import useMenuActions from 'data/hooks/useMenuActions';
import { useBlockRoute, useAskModal } from 'components/Modals/AskBeforeLeave';
import usePrevious from 'utils/usePrevious';
import { Items } from 'model/menu';

const defaultInitialValues = {
  structure: [],
  modifierGroups: {},
  items: {},
  status: 'ACTIVE',
  modal: undefined,
  hasChanges: false,
};

const ignoreRoutePattern = (pathname: string) =>
  pathname.includes('menu-manager');

const useConnect = () => {
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [nextBrandId, setNextBrandId] = useState<string>('');
  const previousSelectedBrandId = usePrevious(selectedBrandId);
  const { data, isLoading, refetch } = useMenu(selectedBrandId);
  const { update, exportDraft, exportLoading } = useMenuActions();

  const handleUpdate = () => {
    if (data) {
      return update({
        brandId: selectedBrandId,
        menu: {
          id: data.id,
          brandId: data.brandId,
          structure: data.structure,
          modifierGroups: data.modifierGroups,
          items: Object.keys(data.items).reduce<Items>(
            (acum, itemKey) => ({
              ...acum,
              [itemKey]: {
                ...data.items[itemKey],
                price: data.items[itemKey].price,
              },
            }),
            {},
          ),
          status: data.status,
        },
      });
    }

    return Promise.resolve();
  };
  const hasUnsavedChanges = !!data?.hasChanges;

  const handleExportFinal = useCallback(
    () => exportDraft({ brandId: selectedBrandId, usePrintNames: false }),
    [selectedBrandId, exportDraft],
  );

  const handleExportInstructions = useCallback(
    () => exportDraft({ brandId: selectedBrandId, usePrintNames: true }),
    [selectedBrandId, exportDraft],
  );

  const unblockSelectedBrandId = useCallback(
    () => setSelectedBrandId(nextBrandId),
    [setSelectedBrandId, nextBrandId],
  );

  const askBeforeLeaveRouteModal = useBlockRoute({
    shouldBlock: hasUnsavedChanges,
    onPublish: handleUpdate,
    ignoreRoutePattern,
  });

  const { openModal, ...askBeforeChangeBrandModal } = useAskModal({
    onPublish: handleUpdate,
    onPublishEnd: unblockSelectedBrandId,
    onDiscard: unblockSelectedBrandId,
  });

  const handleSelectBrand = useCallback(
    (newBrandId: string) => {
      setNextBrandId(newBrandId);
      if (hasUnsavedChanges) {
        openModal();
      } else {
        setSelectedBrandId(newBrandId);
      }
    },
    [setSelectedBrandId, openModal, hasUnsavedChanges, setNextBrandId],
  );

  useEffect(() => {
    if (selectedBrandId !== '' && previousSelectedBrandId !== selectedBrandId) {
      refetch();
    }
  }, [refetch, previousSelectedBrandId, selectedBrandId]);

  return {
    selectedBrandId,
    handleSelectBrand,
    data: data || defaultInitialValues,
    isLoading: isLoading || !data,
    handleUpdate,
    askBeforeLeaveRouteModal,
    askBeforeChangeBrandModal,
    handleExportFinal,
    handleExportInstructions,
    exportLoading,
  };
};

export default useConnect;
